/**
 * ONE planned day, as it is being walked — the Route Planner's execution side
 * (PLAN_EXECUTION_2026-09-07 §1 Fix B).
 *
 * Renders from `GET /tour-plans/{id}/day/{date}/progress` and from nothing
 * else: the tick beside a doctor, the "N of M visited" line and the unplanned
 * count are the SERVER's answer, computed by the same function the Sales
 * Officer dashboard's "Today Completed" tile reads (`day_progress_for`). One
 * component per fact — the plan page and the dashboard both mount this.
 *
 * "Mark visited" is a LINK to the DCR form with the doctor and the planned
 * day filled in. It writes nothing here and nothing to the plan: the visit is
 * the record, and an approved plan stays read-only. Coming back to this page
 * re-fetches, which is how the tick appears.
 *
 * Three shapes of "nothing to tick", kept apart (F313 / Pattern A):
 *   - no entry for the day        → "Nothing planned for this day."
 *   - entry, no doctor list ever  → DOCTORS_NOT_NAMED (a gap, not a zero)
 *   - entry, empty list           → "0 of 0 visited", no rows — a statement
 * A failed fetch renders the error, never an empty card that reads as "nothing
 * planned".
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Loader2, CalendarCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { formatISTDayLabel } from "../../lib/datetime";
import { plural } from "../../lib/vocabulary";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import CategoryBadge from "../doctors/CategoryBadge";
import {
  DOCTORS_NOT_NAMED,
  DOCTORS_NOT_NAMED_HELP,
  DOCTOR_NOT_LISTED,
} from "../../pages/tour-plans/plannedDoctors";

export function markVisitedHref(doctorId, date) {
  return `/doctor-visits/new?doctor=${encodeURIComponent(doctorId)}&plan_date=${encodeURIComponent(date)}`;
}

export default function PlannedDayCard({ planId, date, title = "Today's plan" }) {
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(!!planId);

  useEffect(() => {
    if (!planId || !date) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { data } = await api.get(`/tour-plans/${planId}/day/${date}/progress`);
        if (!cancelled) setProgress(data);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [planId, date]);

  const counts = progress?.counts;

  return (
    <Card className="bg-white border-callisto shadow-sm rounded-xl" data-testid="planned-day-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3">
        <CardTitle className="text-base text-callisto-navy flex items-center gap-2">
          <CalendarCheck className="h-4 w-4 text-callisto-gold" />
          {title}
        </CardTitle>
        <span className="text-[11px] uppercase tracking-wider text-callisto-muted">
          {formatISTDayLabel(date)}
        </span>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {!planId ? (
          <p className="text-sm text-callisto-muted" data-testid="planned-day-card-empty">
            No approved plan covers this day. Plan the month in Route Planner, and today's
            doctors will list here with a Mark visited button each.
          </p>
        ) : loading ? (
          <p className="text-sm text-callisto-muted flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading today's plan…
          </p>
        ) : error ? (
          <p className="text-sm text-red-700" data-testid="planned-day-card-error">
            Today's plan did not load — {error}
          </p>
        ) : !progress?.day_planned ? (
          <p className="text-sm text-callisto-muted" data-testid="planned-day-progress">
            Nothing planned for this day.
            {counts?.unplanned ? ` ${plural(counts.unplanned, "unplanned call")} filed.` : ""}
          </p>
        ) : !progress.doctors_named ? (
          <p className="text-sm text-callisto-muted" data-testid="planned-day-progress" title={DOCTORS_NOT_NAMED_HELP}>
            {DOCTORS_NOT_NAMED}. {DOCTORS_NOT_NAMED_HELP}
          </p>
        ) : (
          <>
            <p className="text-sm font-semibold text-callisto-navy" data-testid="planned-day-progress">
              {counts.visited} of {counts.planned} visited
              {counts.unplanned > 0 && (
                <span className="ml-2 font-normal text-callisto-muted" data-testid="planned-day-unplanned">
                  · {plural(counts.unplanned, "unplanned call")}
                </span>
              )}
            </p>
            {progress.doctors.length === 0 ? (
              <p className="text-sm text-callisto-muted">No doctors named for this day.</p>
            ) : (
              <ul className="divide-y divide-callisto" data-testid="planned-day-doctors">
                {progress.doctors.map((d) => (
                  <li
                    key={d.doctor_id}
                    className="py-2 flex items-center justify-between gap-3"
                    data-testid={`planned-day-doctor-${d.doctor_id}`}
                    data-visited={d.visited ? "true" : "false"}
                  >
                    <div className="min-w-0 flex items-center gap-2">
                      {d.visited ? (
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-label="Visited" />
                      ) : (
                        <Circle className="h-4 w-4 shrink-0 text-slate-300" aria-hidden="true" />
                      )}
                      <span className={`text-sm truncate ${d.doctor_name ? "font-semibold text-callisto-navy" : "italic text-callisto-muted"}`}>
                        {d.doctor_name || DOCTOR_NOT_LISTED}
                      </span>
                      {d.category && <CategoryBadge category={d.category} size="sm" />}
                    </div>
                    {d.visited ? (
                      <Link
                        to={`/doctor-visits/${d.visit_id}`}
                        className="text-xs font-semibold text-emerald-700 hover:underline shrink-0"
                        data-testid={`visited-${d.doctor_id}`}
                      >
                        Visited
                      </Link>
                    ) : (
                      <Link
                        to={markVisitedHref(d.doctor_id, date)}
                        className="shrink-0 inline-flex items-center h-8 px-3 rounded-md bg-callisto-navy text-white text-xs font-semibold hover:bg-callisto-navy/90"
                        data-testid={`mark-visited-${d.doctor_id}`}
                      >
                        Mark visited
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
